import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, Pencil, Calendar, ShieldCheck, AlertTriangle } from 'lucide-react';

const LICENSE_TYPES = ['LBSW', 'LSW', 'LMSW', 'LCSW', 'LICSW', 'LCSW-S', 'LISW', 'Other'];

const daysUntil = (d) => {
  const diff = Math.ceil((new Date(d + 'T00:00:00') - new Date()) / 86400000);
  return diff;
};

const MyLicenseReminders = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [states, setStates] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ license_type: 'LCSW', state: params.get('state') || '', license_number: '', expiration_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => { document.title = 'My License Renewal Reminders | I Need A Social Worker'; }, []);
  useEffect(() => { if (!authLoading && !isAuthenticated) navigate('/login'); }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => { axios.get(`${API_URL}/license-center/states`).then((r) => setStates(r.data.states || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/license-center/reminders`);
      setReminders(r.data.reminders || []);
      setConsentAt(r.data.disclaimer_accepted_at || null);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setEditing(null); setForm({ license_type: 'LCSW', state: '', license_number: '', expiration_date: '', notes: '' }); };

  const save = async () => {
    if (!form.state) { toast.error('Please choose a state.'); return; }
    if (!form.expiration_date) { toast.error('Please enter your license expiration date.'); return; }
    if (!consentAt && !agreed) { toast.error('Please check "I understand" to activate reminders.'); return; }
    setSaving(true);
    try {
      if (!consentAt && agreed) {
        try { const c = await axios.post(`${API_URL}/license-center/reminders/consent`, {}); setConsentAt(c.data.accepted_at); } catch (e) { /* noop */ }
      }
      if (editing) await axios.put(`${API_URL}/license-center/reminders/${editing}`, form);
      else await axios.post(`${API_URL}/license-center/reminders`, form);
      toast.success(editing ? 'Reminder updated.' : 'Renewal reminder saved.');
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save reminder.'); } finally { setSaving(false); }
  };

  const edit = (r) => { setEditing(r.reminder_id); setForm({ license_type: r.license_type, state: r.state, license_number: r.license_number || '', expiration_date: r.expiration_date, notes: r.notes || '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const remove = async (id) => { try { await axios.delete(`${API_URL}/license-center/reminders/${id}`); toast.success('Reminder deleted.'); load(); } catch (e) { toast.error('Failed.'); } };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="inline-flex items-center gap-2 text-secondary font-semibold uppercase text-xs tracking-widest"><Bell className="w-4 h-4" /> Renewal Reminders</p>
          <h1 className="font-serif text-3xl font-bold mt-1">My License Renewal Reminders</h1>
          <p className="text-white/80 mt-2">Add your license details and we'll email you at 90, 60, 30, and 7 days before it expires.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Courtesy disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="reminder-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            <strong>Important:</strong> License reminders are provided as a courtesy and are not guaranteed.
            You remain responsible for monitoring and renewing your professional license with your state licensing board.
          </p>
        </div>

        {/* Add / edit form */}
        <div className="bg-white rounded-2xl border p-6 mb-6">
          <h2 className="font-serif text-lg font-bold text-primary mb-4">{editing ? 'Edit Reminder' : 'Add a License'}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>License type</Label>
              <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={form.license_type} onChange={(e) => setForm((f) => ({ ...f, license_type: e.target.value }))} data-testid="reminder-type-select">
                {LICENSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>State</Label>
              <select className="w-full border rounded-md h-10 px-3 text-sm bg-white" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} data-testid="reminder-state-select">
                <option value="">Select state</option>
                {states.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>License number (optional)</Label>
              <Input value={form.license_number} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} placeholder="e.g. 12345" data-testid="reminder-number-input" />
            </div>
            <div>
              <Label>Expiration date</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} data-testid="reminder-date-input" />
            </div>
          </div>
          <div className="mt-4">
            <Label>Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. CE hours still needed" data-testid="reminder-notes-input" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
            {!consentAt ? (
              <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer" data-testid="reminder-consent-label">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="reminder-consent-checkbox" />
                <span>I understand these reminders are a courtesy, are not guaranteed, and I am responsible for renewing my license on time with my state board.</span>
              </label>
            ) : <span />}
            <div className="flex justify-end gap-2 shrink-0">
              {editing && <Button variant="outline" onClick={resetForm}>Cancel</Button>}
              <Button onClick={save} disabled={saving} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="reminder-save-btn"><Plus className="w-4 h-4 mr-1" /> {editing ? 'Update Reminder' : 'Save Reminder'}</Button>
            </div>
          </div>
        </div>

        {/* Reminders list */}
        <h2 className="font-serif text-lg font-bold text-primary mb-3">My Licenses ({reminders.length})</h2>
        {loading ? (
          <div className="py-10 text-center text-gray-500">Loading…</div>
        ) : reminders.length === 0 ? (
          <div className="bg-white rounded-2xl border p-8 text-center text-gray-500" data-testid="no-reminders">
            <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            No licenses added yet. Add one above to start getting renewal reminders.
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map((r) => {
              const d = daysUntil(r.expiration_date);
              const urgent = d <= 30;
              return (
                <div key={r.reminder_id} className="bg-white rounded-2xl border p-5 flex items-start justify-between gap-3" data-testid={`reminder-${r.reminder_id}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-primary">{r.license_type}</span>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{r.state_name}</span>
                      {r.license_number && <span className="text-xs text-gray-400">#{r.license_number}</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Expires {new Date(r.expiration_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <p className={`text-xs mt-1 font-semibold ${d < 0 ? 'text-red-600' : urgent ? 'text-orange-600' : 'text-gray-400'}`}>{d < 0 ? `Expired ${Math.abs(d)} days ago` : `${d} days remaining`}</p>
                    {r.notes && <p className="text-xs text-gray-500 mt-1">{r.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => edit(r)} data-testid={`edit-reminder-${r.reminder_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(r.reminder_id)} className="text-red-600 border-red-200" data-testid={`delete-reminder-${r.reminder_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-6">We store your license details privately to send your reminders. Requirements change — always confirm renewal details with your official state licensing board.</p>
      </div>
    </div>
  );
};

export default MyLicenseReminders;
